/**
 * @file Supposed to be used as a single place for all the theming settings. As well as the root theme provider itself
 */
import React, { FunctionComponent, PropsWithChildren } from 'react';
import { createTheme, ThemeProvider } from '@mui/material';

export const eUkraineFamily = '"e-Ukraine", "Helvetica", "Arial", sans-serif';
export const UnboundedFamily = '"Unbounded", "Helvetica", "Arial", sans-serif';

type RootThemeProps = PropsWithChildren;
const theme = createTheme({
	typography: {
		fontFamily: eUkraineFamily,
		h1: {
			fontFamily: UnboundedFamily,
			fontWeight: 700,
			fontSize: '3rem',
			lineHeight: 1
		},
		h2: {
			fontFamily: eUkraineFamily,
			fontWeight: 500,
			fontSize: '1.875rem',
			lineHeight: '2.25rem'
		},
		h3: {
			fontFamily: UnboundedFamily,
			fontSize: '1rem',
			fontWeight: 500,
			lineHeight: 1.5
		},
		h4: {
			fontFamily: UnboundedFamily,
			fontSize: 14,
			fontWeight: 500,
			lineHeight: 1.2
		},
		h5: {
			fontFamily: eUkraineFamily,
			fontSize: '.875rem',
			lineHeight: '1.25rem',
			fontWeight: 500,
			color: 'rgb(115 115 115)'
		},
		body1: {
			fontFamily: eUkraineFamily,
			fontSize: 14,
			fontWeight: 400,
			lineHeight: 1.2
		},
		body2: {
			fontFamily: eUkraineFamily,
			fontSize: 14,
			fontWeight: 300,
			lineHeight: 1.6
		},
		button: {
			fontFamily: UnboundedFamily,
			fontWeight: 400,
			fontSize: 14
		},
		label: {
			fontFamily: eUkraineFamily,
			fontWeight: 300,
			fontSize: 12,
			lineHeight: 1.2
		},
		p1: {
			fontFamily: eUkraineFamily,
			fontWeight: 300,
			fontSize: 12,
			lineHeight: 1.6
		},
		labelMedium: {
			fontFamily: UnboundedFamily,
			fontWeight: 500,
			fontSize: 12,
			lineHeight: 1.2
		},
	},
	colors: {
		blueTransparentLight: 'rgba(36, 57, 106, 0.04)',
		blueTransparent: 'rgba(36, 57, 106, 0.16)',
		orange: '#FF7B43',
		orangePressed: '#E05920',
		blue: '#24396A',
		bluePressed: '#0B1E4A',
		redPressed: '#CC162C',
		black: '#0A0A0A',
		darkGray: '#4A4E55',
		gray: '#9D9D9D',
		grayMedium: '#666666',
		grayLight: '#F6F7F9',
		divider: '#EFF0F3',
		white: '#FFFFFF',
		red: '#F8223C',
		green: '#4CAF50'
	},
	shape: {
		borderRadius: 12
	},
	palette: {
		divider: '#EFF0F3',
		common: {
			black: '#0A0A0A',
			white: '#FFFFFF'
		}
	},
	components: {
		MuiTypography: {
			defaultProps: {
				variantMapping: {
					labelMedium: 'p',
					label: 'p'
				}
			}
		},
		MuiButtonBase: {
			defaultProps: {
				disableRipple: true
			}
		},
		MuiCssBaseline: {
			styleOverrides: {
				html: {
					height: '100%',
					backgroundColor: '#FFFFFF',
				},
				body: {
					height: '100%',
					backgroundColor: '#EFF0F3',
				}
			}
		}
	}
});


theme.typography.h1 = {
	...theme.typography.h1,
	[theme.breakpoints.down('sm')]: {
		fontSize: 20
	}
};


export const RootThemeProvider: FunctionComponent<RootThemeProps> = props => (
	<ThemeProvider theme={theme}>
		{props.children}
	</ThemeProvider>
);
